import React, { useCallback, useState, type ChangeEvent } from "react";
import { GridRow } from "@design-system/Grid/GridRow";
import { GridCol } from "@design-system/Grid/GridCol";
import { TextArea } from "@design-system/TextArea/TextArea";
import styles from "./MemoSection.module.scss";

interface MemoSectionProps {
    memo: string | null;
    onUpdateMemo: (memo: string | null) => Promise<void>;
    className?: string;
    label?: string;
    labelClassName?: string;
    valueClassName?: string;
}

export const MemoSection: React.FC<MemoSectionProps> = ({
    memo,
    onUpdateMemo,
    className,
    label,
    labelClassName,
    valueClassName,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [memoValue, setMemoValue] = useState(memo ?? "");
    const [displayMemo, setDisplayMemo] = useState(memo ?? null);
    const [previousMemoProp, setPreviousMemoProp] = useState(memo);

    if (memo !== previousMemoProp) {
        setPreviousMemoProp(memo);
        setDisplayMemo(memo ?? null);
        setMemoValue(memo ?? "");
        setIsEditing(false);
    }

    const handleSubmit = useCallback(async () => {
        setIsEditing(false);
        const trimmed = memoValue.trim();
        const newMemo = trimmed.length > 0 ? trimmed : null;
        if (newMemo === displayMemo) {
            return;
        }
        const previousMemo = displayMemo;
        setDisplayMemo(newMemo);
        try {
            await onUpdateMemo(newMemo);
        } catch {
            setDisplayMemo(previousMemo);
        }
    }, [memoValue, displayMemo, onUpdateMemo]);

    const handleClick = useCallback(() => {
        setMemoValue(displayMemo ?? "");
        setIsEditing(true);
    }, [displayMemo]);

    const sharedTextAreaProps = {
        maxLength: 1000,
        placeholder: "Add memo",
        value: memoValue,
        onChange: (event: ChangeEvent<HTMLTextAreaElement>) =>
            setMemoValue(event.target.value),
        onBlur: handleSubmit,
        autoFocus: true,
    };

    if (label) {
        return (
            <GridRow gap="0" className={className}>
                <GridCol sm={4} className={labelClassName}>
                    {label}
                </GridCol>
                <GridCol sm={8} className={valueClassName}>
                    {isEditing ? (
                        <TextArea
                            size="md"
                            autoGrow
                            minRows={1}
                            className={styles.memoEditor}
                            {...sharedTextAreaProps}
                        />
                    ) : (
                        <button
                            type="button"
                            className={styles.metadataValueText}
                            onClick={handleClick}
                        >
                            {displayMemo ?? (
                                <span className={styles.memoPlaceholder}>
                                    Add memo
                                </span>
                            )}
                        </button>
                    )}
                </GridCol>
            </GridRow>
        );
    }

    return (
        <div className={className}>
            {isEditing ? (
                <TextArea size="xl" rows={3} {...sharedTextAreaProps} />
            ) : (
                <button
                    type="button"
                    className={styles.memoDisplay}
                    onClick={handleClick}
                >
                    {displayMemo ? (
                        <>
                            <span className={styles.memoLabel}>Memo</span>
                            <span className={styles.memoValue}>
                                {displayMemo}
                            </span>
                        </>
                    ) : (
                        <span className={styles.memoPlaceholder}>Add memo</span>
                    )}
                </button>
            )}
        </div>
    );
};
